/**
 * The emplacement (WORKBOARD E2 / S6's seat, core/seats.ts): a launcher on a turret that one
 * person sits in and fires — Toy Soldiers' "drop into this gun". A stand-in against the style
 * sheet's rules (§10.2): sized from the **person** (a seat is a person-scale thing, so it scales
 * with k_human, never with surfacePropScale), one hue in three values, a foundation skirt that
 * reaches below the ground so it never floats on a slope, and a silhouette tier.
 *
 * Built in person units (a person is 1.8 tall) and scaled by `personHeight / 1.8`. Faces +Z.
 * Rig: root (scale) → base (static, skirt) → turret (yaw) → cradle (pitch) → tube (muzzle);
 * `seat` marks where the occupant's hips go (a PersonModel in state 'seated' faces the turret's +Z).
 *
 * Crewing (rendering/crewCycle.ts): the tube slides back on `recoil`; the gun-layer cranks `wheel`
 * (its two grips are where the hands go); a loader carries `round` from the ammo crate to the
 * breech (`placeRound`, its two grips for the hands). Presentation only.
 *
 * Design reference: design_sheets/standins.html
 */
import * as THREE from 'three';
import { REFERENCE } from '../constants';
import { bakeStaticParts, markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import type { PersonTier } from './personModel';

export const EMPLACEMENT_UNITS = { width: 2.2, height: 1.9 };   // footprint and top of the shield, person units

/**
 * 63.4's future, partial destruction (ARCHITECTURE §5.13, X34: "a shield: whole, holed, breached"): the emplacement's
 * forms between whole and wrecked. The shield is two plates either side of the tube's slot, each its own group
 * (`shield:left`, `shield:right`: a cover part), so a form can hole, tear or drop one. The gun keeps its place and
 * keeps firing (decision 141: a damaged gun is less accurate, silent only when destroyed), its cradle named `barrel`
 * (the function part):
 * - `holed`: both plates holed; the left one torn down to 0.6 of its height, a shard standing at its slot edge;
 * - `breached`: the left plate gone, lying on the ground in front of the plinth (`fallen:shield-left`: a detachable
 *   piece); the right one holed, torn down to under two thirds and bent back. The shield stops what it covered only up
 *   to the breach.
 */
export type EmplacementForm = 'whole' | 'holed' | 'breached' | 'shieldless';   // 63.4 step 7: shieldless, in play — the whole shield fell (drawn as debris where the sim laid it); not one of the sheet's steps
export const EMPLACEMENT_FORMS: EmplacementForm[] = ['whole', 'holed', 'breached'];
/** The shield's plates (person units): each from the slot's edge to the shield's edge, its bottom, its height. */
const SHIELD = { slot: 0.22, edge: 0.81, bottom: 0.505, height: 0.95, z: 0.62, thick: 0.1 };
const HOLE = '#1c1c20';

export interface EmplacementModel {
  root: THREE.Group;
  full: THREE.Group;
  silhouette: THREE.Group;
  turret: THREE.Group;      // rotation.y = yaw
  cradle: THREE.Group;      // rotation.x = −pitch
  muzzle: THREE.Object3D;
  seat: THREE.Object3D;     // occupant's hip anchor, in turret space
  recoil: THREE.Group;      // the tube; slides back along −Z on a shot (setEmplacementRecoil)
  wheel: THREE.Group;       // the elevating handwheel; rotation.z = crank angle
  wheelGrips: [THREE.Object3D, THREE.Object3D];
  crate: THREE.Object3D;    // where a round is lifted from (turret space)
  breech: THREE.Object3D;   // where a round goes in (cradle space: follows the pitch)
  round: THREE.Group;       // a round in the loader's hands (placeRound); hidden when none is carried
  roundGrips: [THREE.Object3D, THREE.Object3D];
  tier: PersonTier;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}
function cyl(parent: THREE.Object3D, rTop: number, rBot: number, h: number, sides: number, color: string): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, sides), mat(color));
  parent.add(m);
  return m;
}

/**
 * @param personHeight a person's height in world units (1.1 classic, 0.055 at k_human = 1/20)
 * @param skirtDepth how far the foundation reaches below the ground, person units — enough for
 *        the steepest slope the emplacement may stand on (§10.2 rule 5)
 */
export function buildEmplacementModel(p: UnitPalette, personHeight: number, skirtDepth = 0.6, opts: { form?: EmplacementForm } = {}): EmplacementModel {
  const form = opts.form ?? 'whole';
  const root = new THREE.Group();
  root.scale.setScalar(personHeight / 1.8);
  const full = new THREE.Group(); root.add(full);

  // Base: a low octagonal plinth in the dark, and a skirt of the same below ground
  const plinth = cyl(full, 1.05, 1.15, 0.22, 8, p.dark); plinth.position.y = 0.11;
  const skirt = cyl(full, 1.15, 1.2, skirtDepth, 8, p.dark); skirt.position.y = -skirtDepth / 2;
  const plinthFlash = box(full, 0.5, 0.08, 0.12, p.flash, 0, 0.18, 0.98);    // a team flash on the plinth's front
  // 63.3's follow-up (lane F): the measured shape's parts, each its own box (hitShapeFit.ts); the thin parts left out
  for (const m of [plinth, skirt, plinthFlash]) m.userData.fitPart = 'plinth';
  const sock = new THREE.Object3D(); sock.position.set(1.7, 0, -1.3); sock.userData.windMount = 'sock'; full.add(sock);   // 67.36: the pit's windsock, behind its plinth

  // Turret (yaw): carriage, seat, shield
  const turret = new THREE.Group(); turret.name = 'turret'; turret.position.y = 0.22; full.add(turret);   // (63.1f's follow-up: named — the host lays it by its gunner's bearing)
  const ring = cyl(turret, 0.7, 0.75, 0.12, 10, p.metal); ring.position.y = 0.06;
  const carriage = [ring, box(turret, 1.2, 0.34, 1.1, p.body, 0, 0.29, 0.05),   // carriage
    box(turret, 0.16, 0.6, 0.6, p.body, -0.52, 0.66, 0.1),                     // trunnion cheeks
    box(turret, 0.16, 0.6, 0.6, p.body, 0.52, 0.66, 0.1)];
  for (const m of carriage) m.userData.fitPart = 'carriage';
  // the shield: two plates either side of the tube's slot (63.4's forms hole, tear and drop them), the flash along each top
  const w = SHIELD.edge - SHIELD.slot;
  const plate = (side: -1 | 1, height: number, holes: number, lean = 0): THREE.Group => {
    const g = new THREE.Group(); g.name = side < 0 ? 'shield:left' : 'shield:right';
    g.position.set(side * (SHIELD.slot + w / 2), SHIELD.bottom, SHIELD.z); g.rotation.x = lean; turret.add(g);
    box(g, w - 0.01, height, SHIELD.thick, p.body, 0, height / 2, 0);
    if (height === SHIELD.height) box(g, w, 0.09, 0.12, p.flash, 0, 0.895, 0);   // the flash along its top (gone where it is torn)
    for (let i = 0; i < holes; i++) box(g, 0.13, 0.11, SHIELD.thick + 0.02, HOLE, (i % 2 ? 0.12 : -0.1) * side, height * (0.3 + 0.35 * (i % 2)), 0);
    return g;   // its meshes are the measured `shield` part (63.3's follow-up, lane F), tagged once the form is built
  };
  if (form === 'whole') { plate(-1, SHIELD.height, 0); plate(1, SHIELD.height, 0); }
  else if (form === 'holed') {
    const tear = SHIELD.height * 0.6, left = plate(-1, tear, 2);
    const shard = box(left, 0.12, 0.22, SHIELD.thick, p.body, w / 2 - 0.08, tear + 0.04, 0); shard.rotation.z = -0.18;   // a shard at its slot edge
    box(left, w * 0.6, 0.09, 0.12, p.flash, -0.1, tear, 0.02).rotation.z = 0.2;   // its flash edge, bent down with it
    plate(1, SHIELD.height, 2);
  } else if (form === 'shieldless') {
    // 63.4 step 7: the shield blown off whole — its mounting lugs and a sheared strip along each plate's foot are left
    for (const side of [-1, 1] as const) {
      box(turret, 0.12, 0.14, 0.14, p.metal, side * (SHIELD.slot + 0.08), SHIELD.bottom + 0.05, SHIELD.z);
      box(turret, 0.12, 0.14, 0.14, p.metal, side * (SHIELD.edge - 0.08), SHIELD.bottom + 0.05, SHIELD.z);
      const strip = box(turret, w * 0.7, 0.07, SHIELD.thick, p.body, side * (SHIELD.slot + w * 0.45), SHIELD.bottom + 0.035, SHIELD.z); strip.rotation.z = side * 0.06;
    }
  } else {
    plate(1, SHIELD.height * 0.6, 3, -0.25);                                   // holed, torn down and bent back toward the gun
    const fallen = new THREE.Group(); fallen.name = 'fallen:shield-left'; fallen.position.set(-0.9, 0.05, 2.15); fallen.rotation.order = 'YXZ'; fallen.rotation.set(-Math.PI / 2 + 0.08, 0.5, 0); full.add(fallen);
    box(fallen, w - 0.01, SHIELD.height, SHIELD.thick, p.body, 0, SHIELD.height / 2, 0);   // lying on its face in front of the plinth
    box(fallen, w, 0.09, 0.12, p.flash, 0, 0.895, 0);
  }
  // 63.3's follow-up (lane F): the plates still standing are the measured shape's `shield` part — the whole form's two
  // plates span the one plate's box exactly; a fallen plate is not on the turret
  for (const c of turret.children) if (c.name.startsWith('shield:')) c.traverse(o => { if ((o as THREE.Mesh).isMesh) o.userData.fitPart = 'shield'; });
  // The gun-layer's seat beside the breech, left of the cheek, with the elevating handwheel in front
  const thin: THREE.Object3D[] = [
    box(turret, 0.42, 0.08, 0.4, p.dark, -0.85, 0.5, -0.35),                   // seat pan
    box(turret, 0.42, 0.42, 0.08, p.dark, -0.85, 0.72, -0.57),                 // seat back
    box(turret, 0.08, 0.36, 0.08, p.metal, -0.85, 0.3, -0.35)];                // seat post
  const seat = new THREE.Object3D(); seat.position.set(-0.85, 0.55, -0.35); turret.add(seat);
  // the handwheel faces the gun-layer, close enough to crank seated (an arm is 0.64)
  const wheel = new THREE.Group(); wheel.position.set(-0.8, 0.86, 0.12); turret.add(wheel); thin.push(wheel);
  wheel.add(new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.035, 4, 10), mat(p.metal)));
  box(wheel, 0.32, 0.03, 0.03, p.metal, 0, 0, 0);                             // a spoke, so the turning reads
  const wheelGrips: [THREE.Object3D, THREE.Object3D] = [new THREE.Object3D(), new THREE.Object3D()];
  wheelGrips[0].position.set(0.16, 0, -0.05); wheelGrips[1].position.set(-0.16, 0, -0.05);
  for (const g of wheelGrips) { box(wheel, 0.04, 0.04, 0.1, p.dark, g.position.x, 0, -0.04); wheel.add(g); }
  thin.push(box(turret, 0.26, 0.06, 0.06, p.metal, -0.66, 0.86, 0.12));      // its shaft into the cheek
  // the ammo crate on a ready rack behind the right cheek, at a loader's waist (an arm is 0.64)
  thin.push(box(turret, 0.08, 0.46, 0.08, p.metal, 0.55, 0.23, -0.62));      // the rack's post
  for (const m of [box(turret, REFERENCE.ammoBox.width, REFERENCE.ammoBox.height, REFERENCE.ammoBox.depth, p.dark, 0.55, 0.59, -0.62),   // 63.2: the reference ammo box
    box(turret, 0.44, 0.04, 0.32, p.flash, 0.55, 0.73, -0.62)]) m.userData.fitPart = 'crate';   // its lid edge
  const crate = new THREE.Object3D(); crate.position.set(0.55, 0.8, -0.62); turret.add(crate);

  // Cradle (pitch) and the launcher tube: wide, short — it fires the lob (EMPLACEMENT_WEAPONS)
  const cradle = new THREE.Group(); cradle.name = 'barrel'; cradle.position.set(0, 0.72, 0.1); turret.add(cradle);
  box(cradle, 0.5, 0.5, 0.75, p.metal, 0, 0, -0.15).userData.fitPart = 'breech';   // breech block
  const breech = new THREE.Object3D(); breech.position.set(0, 0.02, -0.62); cradle.add(breech);
  const recoil = new THREE.Group(); cradle.add(recoil);                       // the tube slides back in the cradle
  const tube = cyl(recoil, 0.17, 0.2, 2.4, 10, p.metal); tube.rotation.x = Math.PI / 2; tube.position.z = 1.05;
  const band = cyl(recoil, 0.21, 0.21, 0.1, 10, p.flash); band.rotation.x = Math.PI / 2; band.position.z = 1.9;
  thin.push(tube, band);
  const muzzle = new THREE.Object3D(); muzzle.position.z = 2.25; recoil.add(muzzle);

  // a round: a short fat shell with a flash band, carried in two hands
  const round = new THREE.Group(); round.visible = false; turret.add(round); thin.push(round);
  const shell = cyl(round, 0.12, 0.12, 0.42, 8, p.metal); shell.rotation.x = Math.PI / 2;
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.14, 8), mat(p.metal)); nose.rotation.x = Math.PI / 2; nose.position.z = 0.28; round.add(nose);
  const rband = cyl(round, 0.125, 0.125, 0.05, 8, p.flash); rband.rotation.x = Math.PI / 2; rband.position.z = 0.12;
  const roundGrips: [THREE.Object3D, THREE.Object3D] = [new THREE.Object3D(), new THREE.Object3D()];
  roundGrips[0].position.set(0.13, -0.02, -0.08); roundGrips[1].position.set(-0.13, -0.02, 0.08);
  round.add(roundGrips[0], roundGrips[1]);

  // 63.3's follow-up: the seat, its handwheel, the rack's post, the tube and a carried round stop neither a shot nor a look
  for (const t of thin) t.traverse(o => { o.userData.thin = true; });

  // The far tier: plinth, shield block, tube
  const silhouette = new THREE.Group(); root.add(silhouette);
  box(silhouette, 2.1, 0.3, 2.1, p.dark, 0, 0.15, 0);
  const shieldTop = form === 'whole' ? 1.4 : form === 'holed' ? 1.2 : form === 'shieldless' ? 0.75 : 1.05;   // its far tier follows the form: the shield lower as it tears, none when it fell
  box(silhouette, 1.5, shieldTop - 0.3, 1.2, p.body, 0, 0.3 + (shieldTop - 0.3) / 2, 0.15);
  box(silhouette, 0.36, 0.36, 2.2, p.metal, 0, 1.0, 1.2);
  silhouette.visible = false;

  const model: EmplacementModel = { root, full, silhouette, turret, cradle, muzzle, seat, recoil, wheel, wheelGrips, crate, breech, round, roundGrips, tier: 'full' };
  aimEmplacement(model, 0, 0.6);
  return model;
}

/** Point the gun: yaw about the turret's up, pitch positive up (radians). */
export function aimEmplacement(m: EmplacementModel, yaw: number, pitch: number): void {
  m.turret.rotation.y = yaw;
  m.cradle.rotation.x = -Math.max(-0.1, Math.min(1.35, pitch));
}

/** The tube's slide back on a shot: 0 home, 1 full recoil (a third of a person-length). */
export function setEmplacementRecoil(m: EmplacementModel, k: number): void {
  m.recoil.position.z = -0.32 * Math.max(0, Math.min(1, k));
}

const _from = new THREE.Vector3(), _to = new THREE.Vector3(), _Y = new THREE.Vector3(0, 1, 0);
/**
 * Carry a round: `k` 0 at the crate, 1 at the breech (lifted in an arc, nose turning to the tube);
 * null puts it away. The breech follows the cradle's pitch, so call after aiming.
 */
export function placeRound(m: EmplacementModel, k: number | null): void {
  m.round.visible = k !== null;
  if (k === null) return;
  const u = Math.max(0, Math.min(1, k));
  m.turret.updateMatrixWorld(true);
  _from.copy(m.crate.position);
  m.breech.getWorldPosition(_to); m.turret.worldToLocal(_to);
  m.round.position.lerpVectors(_from, _to, u);
  m.round.position.y += 0.35 * Math.sin(Math.PI * u);                 // up out of the crate, over the carriage
  m.round.quaternion.setFromAxisAngle(_Y, Math.PI / 2)                // lying across the arms…
    .slerp(m.cradle.quaternion, u * u);                                // …turning nose-first into the breech
}

/** Fold static parts per moving group (base, turret, cradle, tube, wheel) and the silhouette. */
const _lh = new THREE.Vector3(), _lq = new THREE.Quaternion();
/**
 * 63.1f's follow-up (the owner in a gun pit: "I can't figure out how to turn the cannon left / right"): a turret (any form's,
 * or the gun-layer's anchors') turned so its gun points along `heading` (world): the bearing in its parent's frame, +Z the
 * gun and a positive yaw toward +X. The sim swings the gun-layer by the same angle about the same axis (SEAT_POINTS' pivot).
 */
export function layTurret(turret: THREE.Object3D, heading: { x: number; y: number; z: number }): void {
  if (!turret.parent) return;
  turret.parent.updateWorldMatrix(true, false);
  const h = _lh.set(heading.x, heading.y, heading.z).applyQuaternion(turret.parent.getWorldQuaternion(_lq).invert());
  turret.rotation.y = Math.atan2(h.x, h.z);
}

export function bakeEmplacementModel(m: EmplacementModel): number {
  let n = bakeStaticParts(m.full, { exclude: [m.turret] }).partsBaked;
  n += bakeStaticParts(m.turret, { exclude: [m.cradle, m.wheel, m.round] }).partsBaked;
  n += bakeStaticParts(m.cradle, { exclude: [m.recoil] }).partsBaked;
  n += bakeStaticParts(m.recoil).partsBaked;
  n += bakeStaticParts(m.wheel).partsBaked;
  n += bakeStaticParts(m.round).partsBaked;
  m.silhouette.visible = true;
  n += bakeStaticParts(m.silhouette).partsBaked;
  setEmplacementTier(m, m.tier);
  return n;
}

export function setEmplacementTier(m: EmplacementModel, tier: PersonTier): void {
  m.tier = tier;
  m.full.visible = tier === 'full';
  m.silhouette.visible = tier === 'silhouette';
}

/**
 * 63.4 step 7: the shield as a piece on its own (person units, unscaled), as it came off the carriage whole: its two plates
 * either side of the slot, the flash along their tops — the long axis x (the shield's width), its face toward +z. The
 * debris view lays it where the sim put it, flat on the ground.
 */
export function buildShieldPiece(p: UnitPalette): THREE.Group {
  const g = new THREE.Group(), w = SHIELD.edge - SHIELD.slot;
  for (const side of [-1, 1] as const) {
    box(g, w - 0.01, SHIELD.height, SHIELD.thick, p.body, side * (SHIELD.slot + w / 2), SHIELD.height / 2, 0);
    box(g, w, 0.09, 0.12, p.flash, side * (SHIELD.slot + w / 2), 0.895, 0);
    box(g, 0.1, 0.1, 0.12, p.metal, side * (SHIELD.slot + 0.05), 0.05, -0.06);   // a torn lug at its foot
  }
  box(g, SHIELD.slot * 2 + 0.02, 0.12, SHIELD.thick * 0.8, p.metal, 0, 0.06, 0);   // the strap that held them across the slot, bent with them
  return g;
}
